import { useContext, useEffect, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  ChannelIcon,
  ChevronDownIcon,
  Dialog,
  DropdownMenu,
  Heading,
  Link,
  Text,
  ExternalLinkIcon,
} from '@buffer-mono/popcorn'
import { ProfessionalCard } from './components/ProfessionalCard'
import { PersonalProfile } from './components/PersonalProfile/PersonalProfile'
import { PersonalProfileCard } from './components/PersonalProfileCard'
import { FacebookAuthConfirmation } from './components/FacebookAuthConfirmation'
import PaywallModal from '../PaywallPrompt/PaywallPrompt'
import { Header } from './components/Header/Header'
import { Footer } from './components/Footer/Footer'

import { trackProfileTypeSelectionViewed } from '../../tracking'
import { InstagramAuthMethod, Service } from '../../types'
import type { OnRedirectParams } from '../../types'
import { UserContext, useUser } from '../../../../../common/context/User'
import { isOneBufferOrganization } from '../../../../../common/utils/user'
import { redirectToUrl } from '../../../../../common/utils/urls'
import { Notifications } from './components/Notifications/Notifications'
import { setConnectionSuccessQueryParamsForCustomChannel } from '../../utils'
import { useDispatch, useSelector } from 'react-redux'
import { resetChannelConnectionsState } from '../../../store/channelConnectionsReducer'
import { AccountType } from '../../../../../common/events/types'
import type { OrchestratorRootState } from '../../../../../common/events/types'

import styles from './InstagramSelectAccountType.module.css'
import footerStyles from './components/Footer/Footer.module.css'
import { useModalManager } from '../../../../../components/ModalManager/hooks/useModalManager'

enum Steps {
  accountType = 'accountType',
  notifications = 'notifications',
  personal = 'personal',
  facebookAuth = 'facebookAuth',
}

export default function InstagramSelectAccountType({
  onRedirect,
}: {
  onRedirect: (args: OnRedirectParams) => void
}): JSX.Element {
  const dispatch = useDispatch()
  const user = useContext(UserContext)
  const { modalData: data } = useModalManager()
  const { prefillDataAfterConnection, skipCelebrationModal } = useSelector(
    (state: OrchestratorRootState) => state.channelConnections,
  )

  const [showPayWallModal, setShowPayWallModal] = useState(false)
  const initialStep =
    data?.accountType === AccountType.personal
      ? Steps.personal
      : Steps.accountType
  const [selectedAccountType, setSelectedAccountType] =
    useState<Steps>(initialStep)
  const [channelId, setChannelId] = useState<string | null>(null)
  const isOneBufferOrg = isOneBufferOrganization(user)
  if (showPayWallModal) {
    return (
      <PaywallModal
        service={Service.instagram}
        authMethod={InstagramAuthMethod.notificationsOnly}
        onClose={(): void => setShowPayWallModal(false)}
      />
    )
  }

  const stepMap = {
    personal: (
      <PersonalProfile
        onBack={(): void => setSelectedAccountType(Steps.accountType)}
        onContinue={(connectedChannelId): void => {
          setChannelId(connectedChannelId)
          setSelectedAccountType(Steps.notifications)
        }}
      />
    ),
    notifications: (
      <Notifications
        onContinue={(): void => {
          // Redirect to '/channels' and trigger channel connection success modal.
          // Forward prefillDataAfterConnection / skipCelebrationModal so the
          // composer-reopen path works after a first-channel Instagram
          // personal-profile connection — OAuth already does this via
          // useAuthorizationInfo, custom channels must do it here.
          if (channelId) {
            redirectToUrl(
              setConnectionSuccessQueryParamsForCustomChannel(channelId, {
                prefillDataAfterConnection,
                skipCelebrationModal,
              }).toString(),
            )
            dispatch(resetChannelConnectionsState({}))
          }
        }}
      />
    ),
    facebookAuth: (
      <FacebookAuthConfirmation
        onBack={(): void => setSelectedAccountType(Steps.accountType)}
        onRedirect={onRedirect}
      />
    ),
    accountType: (
      <SelectAccountType
        onPersonalSelect={(): void => {
          if (isOneBufferOrg) {
            setSelectedAccountType(Steps.personal)
          } else {
            setShowPayWallModal(true)
          }
        }}
        onFacebookSelect={(): void =>
          setSelectedAccountType(Steps.facebookAuth)
        }
        onRedirect={onRedirect}
      />
    ),
  }

  return stepMap[selectedAccountType]
}

function SelectAccountType({
  onPersonalSelect,
  onFacebookSelect,
  onRedirect,
}: {
  onPersonalSelect: () => void
  onFacebookSelect: () => void
  onRedirect: (args: OnRedirectParams) => void
}): JSX.Element {
  const account = useUser()

  useEffect(() => {
    trackProfileTypeSelectionViewed({
      account,
      service: Service.instagram,
    })
  }, [account])

  return (
    <>
      <Header />

      <Dialog.Body className={styles.body}>
        <div className={styles.titleSubtitle}>
          <Heading size="lg" align="center">
            How would you like to connect your Instagram Account?
          </Heading>
          <Text align="center" className={styles.subtitle}>
            Features depend on the type of Instagram account you have and the
            connection you choose.
          </Text>
        </div>
        <div className={styles.cardGrid}>
          <ProfessionalCard onRedirect={onRedirect} />
          <PersonalProfileCard onPersonalSelect={onPersonalSelect} />
        </div>
        <Text
          className={styles.facebookLoginOption}
          icon={<ChannelIcon type="facebook" color="branded" size="small" />}
        >
          <Link
            as="button"
            type="button"
            color="inherit"
            onClick={onFacebookSelect}
          >
            Connect Instagram through Facebook
          </Link>{' '}
          if your Instagram account is currently linked to Facebook.
        </Text>
      </Dialog.Body>
      <Footer>
        <DropdownMenu
          align="start"
          className={footerStyles.menu}
          trigger={
            <Button
              variant="tertiary"
              size="large"
              className={footerStyles.helpButton}
            >
              Help
              <ChevronDownIcon />
            </Button>
          }
        >
          <DropdownMenu.Item asChild>
            <a
              className={footerStyles.helpLink}
              href="https://support.buffer.com/articles/using-instagram-with-buffer-YSjg2dXFV8?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more"
              target="_blank"
              rel="noopener noreferrer"
            >
              Finding your Instagram account type
              <ExternalLinkIcon />
            </a>
          </DropdownMenu.Item>
          <DropdownMenu.Item asChild>
            <a
              className={footerStyles.helpLink}
              href="https://support.buffer.com/articles/using-instagram-with-buffer-YSjg2dXFV8?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more"
              target="_blank"
              rel="noopener noreferrer"
            >
              Converting your Instagram account to a Professional account
              <ExternalLinkIcon />
            </a>
          </DropdownMenu.Item>
        </DropdownMenu>
      </Footer>
    </>
  )
}
